import React, { ReactNode } from 'react';
import styled from 'astroturf/react';
import Dropzone, { DropFilesEventHandler } from 'react-dropzone';

import { DEFAULT_ACCEPTED_FILE_EXTENSIONS } from 'js/utils/file_uploader';

import { Button } from 'js/design_system/Button';

/**
 * A styled file upload component that both accepts drag and drop and
 * a button to open a platform-specific file picker.
 *
 * @param {DropFilesEventHandler} onNewFiles callback for when new files are dropped or selected by the user. Receives both accepted and rejected files.
 * @param {string[]=} [acceptedFileExtensions=DEFAULT_ACCEPTED_FILE_EXTENSIONS] array of file extensions to accept, e.g. ['.pdf', '.jpg']
 * @param {boolean} [disabled=false] Renders in a disabled state, disables the button and drag and drop.
 * @param {boolean} [multiple=true] Allows multiple files to be dropped or selected, and changes the hint.
 * @param {boolean} [primary=false] Whether to render the button as a primary CTA.
 * @param {boolean} [small=false] Renders a small version of the component for use in lists and other space-limited contexts.
 * @param {ReactNode} [children] Optional children to be rendered within the component
 * @param {() => void} [onClick] Optional callback for when file upload is clicked.
 *
 * ## Basic Usage
 * @example
 *
 * ```tsx
 * import FileUpload from 'js/design_system/FileUpload';
 *
 * <FileUpload
 *   onNewFiles={(acceptedFiles, rejectedFiles) => { console.log(acceptedFiles, rejectedFiles) }}
 * />
 * ```
 */

export type FileUploadProps = {
  onNewFiles: DropFilesEventHandler;
  description?: string;
  acceptedFileExtensions?: string[];
  note?: string;
  disabled?: boolean;
  multiple?: boolean;
  primary?: boolean;
  secondary?: boolean;
  small?: boolean;
  style?: object;
  dropzoneStyle?: object;
  children?: ReactNode;
  onClick?: () => void;
  buttonText?: string;
};

const UploadZone = ({
  onNewFiles,
  description,
  acceptedFileExtensions = DEFAULT_ACCEPTED_FILE_EXTENSIONS,
  note,
  disabled = false,
  multiple = true,
  primary = false,
  secondary = false,
  small = false,
  style = {},
  dropzoneStyle = {},
  children,
  onClick,
  buttonText,
}: FileUploadProps) => {
  const acceptedFiles = acceptedFileExtensions
    .map((extension) =>
      // Don't modify mime types or already-prefixed extensions
      extension[0] === '.' || extension.includes('/')
        ? extension
        : `.${extension}`,
    )
    .join(', ');

  return (
    // @ts-ignore - react-dropzone types haven't been updated to handle explicit children from react 18
    <Dropzone
      accept={acceptedFiles}
      disableClick={disabled}
      onDrop={disabled ? undefined : onNewFiles}
      multiple={multiple}
      style={{ maxWidth: '100%', flex: 1, ...dropzoneStyle }}
      onClick={disabled ? undefined : onClick}
    >
      {children}

      <DropzoneBody style={style}>
        <DropzoneLabel compact={small}>
          Drag and drop{' '}
          {description ? (
            <strong>{description}</strong>
          ) : multiple ? (
            'files'
          ) : (
            'a file'
          )}
        </DropzoneLabel>
        <DropzoneLabel compact={small}>or</DropzoneLabel>
        <ButtonContainer>
          <Button
            type="button"
            disabled={disabled}
            size={small ? 'sm' : 'lg'}
            secondary={secondary}
            inverse={!primary && !(secondary ?? false)}
          >
            {buttonText || `Choose ${multiple ? 'files' : 'a file'}`}
          </Button>
        </ButtonContainer>
        <Note>{note}</Note>
      </DropzoneBody>
    </Dropzone>
  );
};

export default UploadZone;

const DropzoneLabel = styled.div<{ compact?: boolean }>`
  font-size: 22px;
  font-weight: 400;
  margin-bottom: 8px;
  line-height: 28px;
  text-align: center;

  &.compact {
    font-size: 16px;
    line-height: 20px;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 8px;
`;

const DropzoneBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 540px;
  padding: 24px;
  background-color: #fff;
  border: 3px dashed #dddde2;
`;

export const Note = styled.div`
  @import 'stylesheets/design_system/font';
  @include body__small;
  color: var(--text-secondary);

  margin-top: 16px;
`;
